import { QuestionBucketDownloadDto, QuizDownloadDto } from 'collaboration-service';
import { Button } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { useImgI18N } from 'services/ImgI18N';
import EditPartContainer, { EditPartContainerType } from '../EditPartContainer';
import EditPartLabelAndEditor from '../EditPartLabelAndEditor';
import EditBoolean from '../Editors/EditBoolean';
import EditCategoryLikeMultiple from '../Editors/EditCategoryLikeMultiple';
import EditNumberWithSlider from '../Editors/EditNumberWithSlider';
import EditSelect from '../Editors/EditSelect';
import EditTwoDates from '../Editors/EditTwoDates';
import { useGPEState } from '../Helpers/UseGPEState';
import EditQuizBucket from './EditQuizBucket';

interface PostEditQuizProps {
    epc: EditPartContainerType;
    post: QuizDownloadDto;
    registerClosed?: boolean;
}

const PostEditQuiz = (p: PostEditQuizProps) => {
    const { epc, post, registerClosed } = p;
    const { t } = useImgI18N("admin");

    const { value: buckets, setValue: setBuckets } = useGPEState<QuizDownloadDto>(x => x.questionBuckets);

    const addBucket = React.useMemo(() => () => {
        const copy: QuestionBucketDownloadDto[] = _.clone(buckets);
        copy.push({
            id: _.uniqueId(),
            selectCount: 0,
            questions: []
        });
        setBuckets(copy);
    }, [buckets, setBuckets]);

    const replaceBucket = React.useMemo(() => (i: number, bucket?: QuestionBucketDownloadDto) => {
        const copy = _.cloneDeep(buckets);
        if (bucket)
            copy.splice(i, 1, bucket);
        else
            copy.splice(i, 1);
        setBuckets(copy);
    }, [buckets, setBuckets]);

    return (
        <>
            <EditPartContainer icon="cog" title="general properties" {...epc} registerClosed={registerClosed}>
                <EditPartLabelAndEditor>
                    {[
                        { label: { icon: "clock", content: "published in between" }, editor: <EditTwoDates<QuizDownloadDto> firstGetter={x => x.publicationDate} secondGetter={x => x.expirationDate} /> },
                        { label: { icon: "tag", content: "categories" }, editor: <EditCategoryLikeMultiple<QuizDownloadDto> getter={x => x.categories} toSelect="categories" /> },
                    ]}
                </EditPartLabelAndEditor>
            </EditPartContainer>
            <EditPartContainer icon="cog" title="quiz properties" {...epc} registerClosed={registerClosed}>
                <EditPartLabelAndEditor>
                    {[
                        {
                            label: { icon: "clock", content: "high score mode" }, editor: <EditSelect<QuizDownloadDto> getter={x => x.highScoreMode} options={[
                                { label: "Optional", value: "Optional" },
                                { label: "Mandatory", value: "Mandatory" },
                                { label: "None", value: "None" },
                            ]} />
                        },
                        { label: { icon: "redo", content: "highscore entry count" }, editor: <EditNumberWithSlider<QuizDownloadDto> getter={x => x.highScoreCount} min={0} max={15} />, height: "60px" },
                        {
                            label: { icon: "clock", content: "show question result" }, editor: <EditSelect<QuizDownloadDto> getter={x => x.questionResultMode} options={[
                                { label: "ShowRightAnswer", value: "ShowRightAnswer" },
                                { label: "ShowResult", value: "ShowResult" },
                                { label: "ShowNothing", value: "ShowNothing" },
                            ]} />
                        },

                        { label: { icon: "tag", content: "hide end result" }, editor: <EditBoolean<QuizDownloadDto> getter={x => x.hideEndResults} /> },

                        {
                            label: { icon: "clock", content: "question reordering" }, editor: <EditSelect<QuizDownloadDto> getter={x => x.questionReorderingMode} options={[
                                { label: "Overall", value: "Overall" },
                                { label: "Bucketwise", value: "Bucketwise" },
                                { label: "Noreordering", value: "Noreordering" },
                            ]} />
                        },


                        { label: { icon: "game", content: "singleplayer allowed" }, editor: <EditBoolean<QuizDownloadDto> getter={x => x.singlePlayerAllowed} /> },
                        { label: { icon: "games", content: "multiplayer allowed" }, editor: <EditBoolean<QuizDownloadDto> getter={x => x.multiPlayerAllowed} /> },

                        { label: { icon: "redo", content: "max repeats allowed" }, editor: <EditNumberWithSlider<QuizDownloadDto> getter={x => x.maxRepeats} min={0} max={15} zeroAsInfinite />, height: "60px" },

                    ]}
                </EditPartLabelAndEditor>
            </EditPartContainer>
            <EditPartContainer icon="cog" title={t("question buckets")} {...epc}>
                <div>
                    <Button icon='add' kind='halfTransparentButton' tooltip={{ tooltipText: t('add new bucket') }} onClick={addBucket} />
                    {_.map(post.questionBuckets, (bucket, i) => <EditQuizBucket
                        bucket={bucket}
                        index={i}
                        replaceBucket={(i, b) => replaceBucket(i, b)}
                    />)}
                </div>
            </EditPartContainer >
        </>
    );
}
export default PostEditQuiz;